import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { SearchX, X } from 'lucide-react';
import Pagination from '../../components/Pagination';
import PostCard from '../../components/PostCard';
import { EmptyState, ErrorState, Loading } from '../../components/StatusStates';
import { usePosts } from '../../hooks/use-public';
import { cn } from '../../lib/format';
import { useSeo } from '../../lib/seo';
import '../../styles/public.css';

const PAGE_SIZE = 6;

export default function Blog() {
  useSeo({
    title: '博客',
    description: '铁头功的博客——记录工程实践、系统设计与学习方法。',
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTag = searchParams.get('tag') ?? undefined;
  const keyword = searchParams.get('keyword')?.trim() || undefined;
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

  const { data, isLoading, isError, refetch, dataUpdatedAt } = usePosts({
    page,
    pageSize: PAGE_SIZE,
    tag: activeTag,
    keyword,
  });

  // 标签云基于首页数据的常用标签不完整，故用一次较大请求汇总标签
  const tagsQuery = usePosts({ page: 1, pageSize: 50 });
  const allTags = useMemo(
    () => Array.from(new Set((tagsQuery.data?.items ?? []).flatMap((post) => post.tags))),
    [tagsQuery.data],
  );

  const updateParams = (next: { tag?: string; keyword?: string; page?: number }) => {
    const params: Record<string, string> = {};
    if (next.tag) params.tag = next.tag;
    if (next.keyword) params.keyword = next.keyword;
    if (next.page && next.page > 1) params.page = String(next.page);
    setSearchParams(params);
  };

  const emptyTitle = keyword
    ? '没有找到相关文章'
    : activeTag
      ? '该标签下暂无文章'
      : '暂无已发布文章';

  return (
    <div className="container content-section">
      <div className="blog-layout">
        <div>
          {(keyword || activeTag) && (
            <div className="filter-summary">
              <span className="filter-summary-label">
                {keyword ? (
                  <>
                    搜索：<strong>{keyword}</strong>
                  </>
                ) : (
                  <>
                    标签：<strong>{activeTag}</strong>
                  </>
                )}
              </span>
              <Link to="/blog" className="filter-summary-clear" aria-label="清除筛选条件">
                <X size={13} aria-hidden="true" />
                清除
              </Link>
            </div>
          )}
          {isLoading ? (
              <Loading />
            ) : isError ? (
              <ErrorState onRetry={() => refetch()} />
            ) : (data?.items ?? []).length === 0 ? (
              keyword ? (
                <div className="search-empty">
                  <SearchX size={34} aria-hidden="true" />
                  <p className="search-empty-title">{emptyTitle}</p>
                  <p className="search-empty-desc">换个关键词试试，或回到博客浏览全部文章。</p>
                  <Link to="/blog" className="btn btn-outline btn-sm">
                    查看全部文章
                  </Link>
                </div>
              ) : (
                <EmptyState title={emptyTitle} description="新的文章完成后将在这里出现。" />
              )
            ) : (
              <>
                <div className="post-list" key={dataUpdatedAt}>
                  {(data?.items ?? []).map((post) => (
                    <PostCard key={post.id} post={post} />
                  ))}
                </div>
                <Pagination
                  page={page}
                  pageSize={PAGE_SIZE}
                  total={data?.total ?? 0}
                  onChange={(nextPage) => updateParams({ tag: activeTag, keyword, page: nextPage })}
                />
              </>
            )}
          </div>

          <aside className="blog-aside">
            <div className="aside-card">
              <p className="aside-heading">标签</p>
              {allTags.length === 0 ? (
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-ink-muted)' }}>
                  暂无标签
                </p>
              ) : (
                <div className="tag-cloud">
                  <button
                    type="button"
                    className={cn('tag tag-clickable', !activeTag && 'tag-active')}
                    onClick={() => updateParams({ keyword, page: 1 })}
                  >
                    全部
                  </button>
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      className={cn('tag tag-clickable', activeTag === tag && 'tag-active')}
                      onClick={() => updateParams({ tag, keyword, page: 1 })}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
  );
}
